import {
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    Injectable,
    Input,
    OnDestroy,
    Optional,
    QueryList,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, FormControlName, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'add-form-field',
    templateUrl: './form-field.component.html',
    styleUrls: ['./form-field.component.scss'],
})
export class AddFormFieldComponent implements OnDestroy, AfterContentInit {
    @Input()
    label: string;
    @Input()
    hintLabel: string;

    errorMessage: string;

    @ContentChildren(FormControlName)
    childFormControlNames: QueryList<FormControlName>;
    @ContentChild(MatFormFieldControl, { static: false })
    matFormFieldControl: MatFormFieldControl<any>;
    @ViewChild(MatFormField, { static: true })
    matFormField: MatFormField;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        @Optional() private form: FormGroupDirective,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    ngAfterContentInit() {
        // https://github.com/angular/material2/issues/9411
        // Angular material has issues with ng-content (with formcontrol) projected inside a mat-form-field
        this.matFormField._control = this.matFormFieldControl;
        this.cdr.detectChanges();

        this.attachStatusChangeListeners();
        this.childFormControlNames.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.attachStatusChangeListeners();
        });
    }

    private attachStatusChangeListeners() {
        if (this.childFormControlNames.length > 1) {
            throw new Error('ut-form-field cannot have more than 1 form control name');
        }

        if (this.childFormControlNames.length === 0) {
            return;
        }

        if (this.childFormControlNames && this.form) {
            const formControlName: FormControlName = this.childFormControlNames.first;
            const formControl: AbstractControl = this.form.getControl(formControlName);
            if (formControl) {
                this.form.ngSubmit.pipe(takeUntil(this.destroy$)).subscribe(() => this.onErrorsChanged(formControl));
                formControl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.onErrorsChanged(formControl));
            }
        }
    }

    private onErrorsChanged(formControl: AbstractControl) {
        const errors = formControl.errors;
        if (!errors) {
            return;
        }

        const errorKeys = Object.keys(errors);
        if (errorKeys.length > 0) {
            const errorKey1 = errorKeys[0];
            const errorObject = errors[errorKey1];
            this.errorMessage = AddFormFieldComponent.toMessage(errorKey1, errorObject);
        }
    }

    private static toMessage(errorKey: string, errorObject: any): string {
        const message = errorObject ? errorObject.message : null;
        if (message) {
            return message;
        }
        const stdMessage: string = AddFormFieldComponent.getStdMessage(errorKey, errorObject);
        if (stdMessage) {
            return stdMessage;
        }
        // nothing left to report but the error key
        return errorKey;
    }

    private static getStdMessage(errorKey: string, errorObject: any): string {
        switch (errorKey) {
            case 'required':
                return $localize`:@@Validation_Required:field is required`;
            case 'email':
                return $localize`:@@Validation_Email:email address is not valid`;
            case 'minlength':
                const msg1 = $localize`:@@Validation_MinLength:min length is`;
                return msg1 + ' ' + errorObject.requiredLength;
            case 'maxlength':
                const msg2 = $localize`:@@Validation_MaxLength:max length is`;
                return msg2 + ' ' + errorObject.requiredLength;
            case 'min':
                const msg3 = $localize`:@@Validation_Min:min is`;
                return msg3 + ' ' + errorObject.min;
            case 'max':
                const msg4 = $localize`:@@Validation_Max:max is`;
                return msg4 + ' ' + errorObject.max;
            case 'pattern':
                if (errorObject.requiredPattern === '/^-?\\d+$/') {
                    return $localize`:@@Validation_PatternDigits:only digits are allowed`;
                }
                return $localize`:@@Validation_Url:url is not valid: the url should be a full url like "https://www.google.com"`;
        }
        return null;
    }
}

@Injectable()
export class AddErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        if (!control) {
            return false;
        }
        // const isSubmitted = form && form.submitted;
        // if (isSubmitted) {
        //     return false;
        // }
        if (!control.touched) {
            return false;
        }
        return control.invalid;
    }
}
